import React, { useCallback, useEffect, useRef } from 'react';

import NavigationElement from 'component/Layout/Slider/__partials/NavigationElement';

import classNames from 'classnames';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import SwiperClass from 'swiper/types/swiper-class';

import { useIntersectionObserver } from 'hooks/useIntersectionObserver';

import 'swiper/scss';

import { SliderProps } from './types';

import styles from './Slider.module.scss';

const Slider = ({
    children,
    settings,
    classListObject,
    navigationOnSlider = false,
    clickHandler,
    onSlideChangeCallback,
    onAfterInitCallback,
    initialIndex = 0,
    setCurrentIndex,
    circledNavigation = false,
    setFocus = false,
    observerOptions = {},
    onSwiper,
    thumbs,
    moduleList,
}: SliderProps) => {
    const swiperRef = useRef<SwiperRef>(null);
    const prevRef = useRef<HTMLDivElement>(null);
    const nextRef = useRef<HTMLDivElement>(null);
    const swiperDivRef = useRef<HTMLDivElement>(null);
    const params = {
        navigation: {
            disabledClass:
                classListObject?.DisabledButton ?? styles.DisabledButton,
        },
        lazyPreloadPrevNext: 2,
        pagination: {
            el: `.${styles.Pagination}`,
            clickable: false,
            bulletClass: classNames(styles.Bullet, classListObject?.Bullet),
        },
        loop: false,
        onClick: function (
            swiper: SwiperClass,
            e: MouseEvent | TouchEvent | PointerEvent
        ) {
            if (clickHandler && e.target instanceof HTMLElement) {
                clickHandler(e, swiper);
            }
        },
        onSlideChange: (swiper: SwiperClass) => {
            onSlideChangeCallback?.(swiper);
            toggleDisabledClass(
                prevRef,
                swiperRef?.current?.swiper.isBeginning
            );
            toggleDisabledClass(nextRef, swiperRef?.current?.swiper.isEnd);
            toggleGradientClass(swiperDivRef, swiperRef?.current?.swiper.isEnd);
        },
        onAfterInit: () => {
            onAfterInitCallback?.();
        },
        onActiveIndexChange: (swiper: SwiperClass) => {
            setCurrentIndex && setCurrentIndex(swiper.realIndex);
        },
    };
    const customSettings = { ...params, ...settings };

    const toggleDisabledClass = useCallback(
        (currentRef?: React.RefObject<HTMLDivElement>, add = true) => {
            if (!params.loop) {
                currentRef?.current?.classList.toggle(
                    styles.DisabledButton,
                    add
                );
                classListObject?.DisabledButton &&
                    currentRef?.current?.classList.toggle(
                        classListObject?.DisabledButton,
                        add
                    );
            }
        },
        [classListObject?.DisabledButton, params.loop]
    );

    const toggleGradientClass = useCallback(
        (currentRef?: React.RefObject<HTMLDivElement>, isEnd = false) => {
            if (!params.loop) {
                classListObject?.FirstGradientHidden &&
                    currentRef?.current?.classList.toggle(
                        classListObject?.FirstGradientHidden,
                        !isEnd
                    );
                classListObject?.LastGradientHidden &&
                    currentRef?.current?.classList.toggle(
                        classListObject?.LastGradientHidden,
                        isEnd
                    );
            }
        },
        [
            classListObject?.FirstGradientHidden,
            classListObject?.LastGradientHidden,
            params.loop,
        ]
    );

    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
        const keyPressed = event.key;
        if (keyPressed === 'ArrowLeft') {
            swiperRef.current?.swiper.slidePrev();
        } else if (keyPressed === 'ArrowRight') {
            swiperRef.current?.swiper.slideNext();
        }
    }, []);

    const focusCallback = useCallback(() => {
        prevRef.current?.focus();
    }, []);

    const intersectionObserver = useIntersectionObserver(
        observerOptions,
        focusCallback
    );

    useEffect(() => {
        prevRef &&
            swiperRef?.current?.swiper.isBeginning &&
            toggleDisabledClass(prevRef);

        nextRef &&
            swiperRef?.current?.swiper.isEnd &&
            toggleDisabledClass(nextRef);

        swiperDivRef && toggleGradientClass(swiperDivRef, false);

        setFocus && intersectionObserver(swiperDivRef.current);
    }, [
        setFocus,
        intersectionObserver,
        prevRef,
        nextRef,
        toggleDisabledClass,
        toggleGradientClass,
    ]);

    return (
        <div
            ref={swiperDivRef}
            className={classNames(
                styles.SliderWrapper,
                {
                    [styles.InnerNavigation]: navigationOnSlider,
                    [styles.CircledNavigation]: circledNavigation,
                },
                classListObject?.SliderWrapper
            )}
        >
            <Swiper
                modules={[Navigation].concat(moduleList ?? [])}
                {...customSettings}
                className={classNames(styles.Slider, classListObject?.Slider)}
                initialSlide={initialIndex}
                ref={swiperRef}
                thumbs={thumbs}
                onSwiper={onSwiper}
            >
                {children.map((slide, index) => (
                    <SwiperSlide
                        className={classNames(
                            styles.Slide,
                            classListObject?.Slide
                        )}
                        key={index}
                    >
                        {slide}
                    </SwiperSlide>
                ))}
                {!!customSettings.pagination && (
                    <div
                        className={classNames(
                            styles.Pagination,
                            classListObject?.Pagination
                        )}
                    />
                )}
            </Swiper>
            {customSettings.navigation && (
                <>
                    <NavigationElement
                        customClass={classNames(
                            classListObject?.PrevButton,
                            styles.ButtonPrev
                        )}
                        ref={prevRef}
                        type="prev"
                        clickHandler={() =>
                            swiperRef.current?.swiper.slidePrev()
                        }
                        onKeyDown={handleKeyDown}
                    />
                    <NavigationElement
                        customClass={classNames(
                            styles.ButtonNext,
                            classListObject?.NextButton
                        )}
                        ref={nextRef}
                        type="next"
                        clickHandler={() =>
                            swiperRef.current?.swiper.slideNext()
                        }
                        onKeyDown={handleKeyDown}
                    />
                </>
            )}
        </div>
    );
};
export default Slider;
